<template>
  <div id="repository-databank" class="relative">
    <div
      id="databank-header"
      class="flex items-center justify-between gap-10 px-6 py-4 border-b bg-pure-white border-slate-400"
    >
      <div class="flex flex-col gap-1">
        <div class="flex items-center gap-2">
          <BlTextTitle3 text="Target Databank" />
          <BlBubbleCount :count="totalCenterTargets || 0" />
        </div>
        <BlTextBody2
          text="Create and save targets that can be utilized for various clients across the center."
        />
      </div>
      <BlInputButton
        v-if="currentUser.role.includes('admin')"
        icon="plus"
        :label="'New Target'"
        @clicked="newOpened = true"
      />
    </div>

    <NewTargetOverlay
      v-if="newOpened"
      title="Create new target in databank"
      kind="center"
      @close-modal="newOpened = false"
    />

    <div v-if="!currentUser.role.includes('admin')" class="w-full px-6">
      <div class="flex items-center gap-2 px-4 py-2 rounded-b bg-info-100">
        <div
          class="grid flex-shrink-0 w-10 h-10 place-items-center"
          :style="{ background: '#E4F1FA', borderRadius: '0.25rem' }"
        >
          <BlIcon icon="toast-info" class="w-6 h-6" />
        </div>
        <BlTextBody1
          class="text-info"
          text="Please note that adding or editing the target databank is restricted to the admin role."
        />
      </div>
    </div>

    <div
      class="sticky z-50 flex items-center gap-2 px-6 py-4 top-18 bg-slate-100"
    >
      <BlInputSearch
        class="w-80"
        :input="searchInput"
        @changed="onInputSearch"
      />
      <div class="w-40">
        <BlInputFilter :input="filterInput" @changed="onChangeFilter" />
      </div>
      <BlSort
        class="w-60"
        v-model="sortInput"
        :options="sortOptions"
        :defaultValue="{ primary: 'date', secondary: 'desc' }"
        @change="onChangeSort($event)"
      />
    </div>

    <div v-if="pageLoading" class="grid h-128 place-items-center">
      <div class="blum-loader"></div>
    </div>
    <div
      v-else-if="!centerTargets.length"
      class="flex flex-col items-center justify-center h-128"
    >
      <BlTextBody1
        class="text-center"
        text="Currently, there are no targets to display."
      />
      <BlTextBody1
        class="text-center"
        text="After creating a new target, it will appear here."
      />
      <BlInputButton
        v-if="currentUser.role.includes('admin')"
        label="Create a new target"
        class="p-0"
        type="plain"
        @clicked="newOpened = true"
      />
    </div>
    <div v-else class="flex flex-col gap-6 px-6 pb-10">
      <div class="border rounded border-slate-400">
        <div
          class="sticky z-10 flex items-center flex-grow-0 w-full top-36 bg-slate-300"
        >
          <div
            class="flex items-center flex-shrink-0 h-12 px-4 border-r rounded-tl border-slate-400"
            :style="{ width: '20%' }"
          >
            <BlTextSubtitle3 text="Name" />
          </div>
          <div
            class="flex items-center flex-shrink-0 h-12 px-4"
            :style="{ width: '15%' }"
          >
            <BlTextSubtitle3 text="Curriculum" />
          </div>
          <div
            class="flex items-center flex-shrink-0 h-12 px-4"
            :style="{ width: '25%' }"
          >
            <BlTextSubtitle3 text="Description" />
          </div>
          <div
            class="flex items-center flex-shrink-0 h-12 px-4"
            :style="{ width: '15%' }"
          >
            <BlTextSubtitle3 text="Data collection method" />
          </div>
          <div
            class="flex items-center flex-shrink-0 h-12 px-4"
            :style="{ width: '15%' }"
          >
            <BlTextSubtitle3 :text="`Goal & success metric`" />
          </div>
          <div
            v-if="currentUser.role.includes('admin')"
            class="flex items-center flex-shrink-0 h-12 px-4 rounded-tr"
            :style="{ width: '10%' }"
          >
            <BlTextSubtitle3 text="" />
          </div>
        </div>
        <DatabankItem
          v-for="target in centerTargets"
          :key="target.id"
          :panelId="panelId"
          :target="target"
          useCurriculum
          :useAction="currentUser.role.includes('admin')"
          @select="onOpenPanel(target)"
        />
      </div>
      <BlInputPagination
        v-if="totalCenterTargets > 25"
        :page="page"
        :perPage="perPage"
        :total="totalCenterTargets"
        @changed="changePage"
        @changed-per-page="changePerPage"
      />
    </div>

    <div
      :data-id="panelId"
      id="panel-details"
      class="fixed flex flex-col justify-between overflow-y-auto transition-all duration-300 border top-18 w-100 bg-pure-white border-slate-400"
      :class="[panelOpened ? 'right-0' : '-right-100']"
      :style="{ height: 'calc(100vh - 4.5rem)', zIndex: 51 }"
    >
      <div v-if="panelLoading" class="grid h-full place-items-center">
        <div class="blum-loader"></div>
      </div>
      <DatabankPanelDetails
        v-else-if="selectedTarget"
        :data-id="panelId"
        :panelId="panelId"
        :target="selectedTarget"
        :openTaskDetails="showTaskDetails"
        :openBehaviorDetails="showBehaviorDetails"
        @editPrompting="editPromptingOpened = true"
        @editTask="editTaskOpened = true"
        @editBehavior="editProblemBehaviorOpened = true"
        @editPromptSbt="editPromptSbtOpened = true"
        @showTaskDetails="showTaskDetails = $event"
        @showBehaviorDetails="showBehaviorDetails = $event"
        @editDetails="editOpened = selectedTarget.id"
      />
      <div
        v-if="
          currentUser.role.includes('admin') &&
            selectedTarget &&
            !showBehaviorDetails &&
            !showTaskDetails
        "
        :data-id="panelId"
        class="sticky bottom-0 z-10 flex items-center gap-4 px-6 py-5 bg-pure-white"
      >
        <div :data-id="panelId" class="flex-grow">
          <BlInputButton
            :data-id="panelId"
            class="w-full"
            type="outlined"
            :label="'Duplicate Target'"
            @clicked="duplicateOpened = selectedTarget.id"
          />
        </div>
        <div
          v-if="!selectedTarget.type.includes('Sbt')"
          :data-id="panelId"
          class="flex-grow"
        >
          <BlInputButton
            :data-id="panelId"
            class="w-full"
            type="outlined"
            :label="'Edit Target'"
            @clicked="editOpened = selectedTarget.id"
          />
        </div>
      </div>
      <div
        v-if="
          currentUser.role.includes('admin') &&
            selectedTarget &&
            selectedTarget.type.includes('Sbt') &&
            showBehaviorDetails
        "
        :data-id="panelId"
        class="sticky bottom-0 z-10 px-6 py-5 bg-pure-white"
      >
        <BlInputButton
          :data-id="panelId"
          class="w-full"
          type="outlined"
          @clicked="
            () => {
              editProblemBehaviorOpened = true;
              showBehaviorDetails = false;
            }
          "
          :label="'Edit problem behaviors'"
        />
      </div>
      <div
        v-if="
          currentUser.role.includes('admin') &&
            selectedTarget &&
            selectedTarget.type.includes('Sbt') &&
            showTaskDetails
        "
        :data-id="panelId"
        class="sticky bottom-0 z-10 px-6 py-5 bg-pure-white"
      >
        <BlInputButton
          :data-id="panelId"
          class="w-full"
          type="outlined"
          @clicked="
            () => {
              editTaskOpened = true;
              showTaskDetails = false;
            }
          "
          :label="'Edit tasks'"
        />
      </div>
    </div>

    <EditPromptsModal
      v-if="editPromptingOpened"
      :targetId="selectedTarget.id"
      :targetName="selectedTarget.name"
      :successMetric="selectedTarget.success_metric"
      :format="selectedTarget.prompting_format"
      :prompts="selectedTarget.prompts"
      @close-modal="editPromptingOpened = false"
    />

    <EditPromptsSbtModal
      v-if="editPromptSbtOpened"
      :targetId="selectedTarget.id"
      :targetName="selectedTarget.name"
      :prompts="selectedTarget.prompts"
      @close-modal="editPromptSbtOpened = false"
    />

    <EditTargetTaskDetailsModal
      v-if="editTaskOpened"
      :tasks="selectedTarget.target_tasks"
      :target="selectedTarget"
      @close-modal="editTaskOpened = false"
    />

    <EditTargetBehaviorDetailsModal
      v-if="editProblemBehaviorOpened"
      @close-modal="editProblemBehaviorOpened = false"
      :target="selectedTarget"
      :targetProblemBehaviors="selectedTarget.target_problem_behaviors"
    />

    <NewTargetOverlay
      v-if="duplicateOpened !== null"
      title="Duplicate a target"
      kind="center"
      :duplicateTargetId="duplicateOpened"
      @close-modal="duplicateOpened = null"
    />

    <EditDatabankModal
      v-if="editOpened !== null"
      :targetId="editOpened"
      @close-modal="editOpened = null"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import debounce from "lodash/debounce";

import NewTargetOverlay from "../clients/target/overlay/NewTargetOverlay.vue";
import DatabankItem from "./databank/Item.vue";
import DatabankPanelDetails from "./databank/PanelDetails.vue";
import EditDatabankModal from "./databank/EditDatabankModal.vue";
import EditTargetTaskDetailsModal from "../clients/target/modal/EditTargetTaskDetailsModal.vue";
import EditTargetDetailsModal from "../clients/target/modal/EditTargetDetailsModal.vue";
import EditPromptsModal from "../clients/target/modal/EditPromptsModal.vue";
import EditPromptsSbtModal from "../clients/target/modal/EditPromptsSbtModal.vue";
import EditTargetBehaviorDetailsModal from "../clients/target/modal/EditTargetBehaviorDetailsModal.vue";

import BlIcon from "../BlIcon.vue";
import BlBubbleCount from "../BlBubbleCount.vue";
import BlInputButton from "../BlInputButton.vue";
import BlInputSearch from "../BlInputSearch.vue";
import BlTextTitle3 from "../BlTextTitle3.vue";
import BlTextSubtitle3 from "../BlTextSubtitle3.vue";
import BlTextBody1 from "../BlTextBody1.vue";
import BlTextBody2 from "../BlTextBody2.vue";
import BlInputPagination from "../BlInputPagination.vue";
import BlInputFilter from "../BlInputFilter.vue";
import BlSort from "../BlSort.vue";

export default {
  name: "RepositoryDatabank",
  components: {
    NewTargetOverlay,
    DatabankItem,
    DatabankPanelDetails,
    EditDatabankModal,
    EditTargetDetailsModal,
    EditTargetTaskDetailsModal,
    EditPromptsModal,
    EditPromptsSbtModal,
    EditTargetBehaviorDetailsModal,
    //
    BlIcon,
    BlBubbleCount,
    BlInputButton,
    BlInputSearch,
    BlTextTitle3,
    BlTextSubtitle3,
    BlTextBody1,
    BlTextBody2,
    BlInputPagination,
    BlInputFilter,
    BlSort
  },
  data() {
    const defaultFilter = [[], []];
    return {
      panelId: "databank-panel",
      pageLoading: true,
      panelOpened: false,
      panelLoading: false,
      selectedTarget: null,
      //
      newOpened: false,
      editOpened: null,
      editPromptingOpened: false,
      editTaskOpened: false,
      editPromptSbtOpened: false,
      editProblemBehaviorOpened: false,
      duplicateOpened: null,
      //
      showTaskDetails: false,
      showBehaviorDetails: false,
      editDetailsOpened: false,
      //
      page: 1,
      perPage: 25,
      searchInput: {
        placeholder: "Search target by name",
        model: ""
      },
      defaultFilter,
      filterInput: {
        model: defaultFilter,
        tabs: [
          {
            name: "Curriculum",
            kind: "multi",
            options: [],
            url: `/api/v1/curriculums?sort=name_asc&query=`,
            fetchPage: 1,
            dataKey: "curriculums",
            countKey: "total_count",
            optionKeys: { value: "id", label: "name", prefixColor: "color" }
          },
          {
            name: "Method",
            kind: "multi",
            options: [
              { name: "Percentage", value: "Target::Percentage" },
              { name: "Duration", value: "Target::Duration" },
              { name: "Partial interval recording", value: "Target::Pir" },
              { name: "Frequency", value: "Target::Frequency" },
              { name: "Prompting", value: "Target::Prompting" },
              { name: "Skill-Based Treatment (SBT)", value: "Target::Sbt" }
            ]
          }
        ]
      },
      sortInput: { primary: "date", secondary: "desc" },
      sortOptions: {
        primary: [
          { value: "name", label: "Name" },
          { value: "curriculum", label: "Curriculum" },
          { value: "type", label: "Data collection method" },
          { value: "date", label: "Date created" }
        ],
        secondary: [
          { value: "asc", label: "Ascending" },
          { value: "desc", label: "Descending" }
        ]
      }
    };
  },
  computed: {
    ...mapGetters(["currentUser", "centerTargets", "totalCenterTargets"]),
    getParams() {
      let params = `?page=${this.page}&per_page=${this.perPage}`;
      const query = this.searchInput.model;
      const curriculumIds = this.filterInput.model
        .at(0)
        .map(a => a.value)
        .join(",");
      const type = this.filterInput.model
        .at(1)
        .map(a => a.value)
        .join(",");
      const sort = this.sortInput;
      if (query) params += `&query=${query}`;
      if (curriculumIds) params += `&curriculum_ids=${curriculumIds}`;
      if (type) params += `&type=${type}`;
      // - sort mapping -
      // name, ascending -> name_asc
      // name, descending -> name_desc
      // curriculum, ascending -> curriculum_asc
      // curriculum, descending -> curriculum_desc
      // data collection method, ascending -> type_asc
      // data collection method, descending -> type_desc
      // date created, ascending -> oldest
      // date created, descending -> newest
      if (sort.primary === "date") {
        params += `&sort=${sort.secondary === "desc" ? "newest" : "oldest"}`;
      } else {
        params += `&sort=${sort.primary}_${sort.secondary}`;
      }
      return params;
    }
  },
  watch: {
    panelOpened(val) {
      if (val) {
        setTimeout(() => {
          window.addEventListener("click", this.closePanelHandler);
        }, 10);
      }
    }
  },
  async mounted() {
    this.pageLoading = true;

    const searchParams = this.$route.query;
    if (searchParams["page"]) {
      this.page = Number(searchParams["page"]);
    }
    if (searchParams["per_page"]) {
      this.perPage = Number(searchParams["per_page"]);
    }
    if (searchParams["query"]) {
      this.searchInput.model = searchParams["query"];
    }
    const filter = [[], []];
    if (searchParams["curriculum_ids"]) {
      const curriculumIds = searchParams["curriculum_ids"].split(",").map(a => {
        const found = this.filterInput.tabs[0].options.find(
          i => i.value === Number(a)
        );
        return found || { value: Number(a) };
      });
      filter[0] = curriculumIds;
    }
    if (searchParams["type"]) {
      const types = searchParams["type"].split(",").map(a => {
        const found = this.filterInput.tabs[1].options.find(i => i.value === a);
        return found || { value: a };
      });
      filter[1] = types;
    }
    this.filterInput.model = filter;
    if (searchParams["sort"]) {
      const temp = searchParams["sort"] || "";
      // prettier-ignore
      const sorts = {
        name_asc: { primary: "name", secondary: "asc" },
        name_desc: { primary: "name", secondary: "desc" },
        curriculum_asc: { primary: "curriculum", secondary: "asc" },
        curriculum_desc: { primary: "curriculum", secondary: "desc" },
        type_asc: { primary: "type", secondary: "asc" },
        type_desc: { primary: "type", secondary: "desc" },
        oldest: { primary: "date", secondary: "asc" },
        newest: { primary: "date", secondary: "desc" }
      };
      if (temp && sorts[temp]) {
        this.sortInput = sorts[temp];
      }
    }

    this.fetchTargets();
  },
  methods: {
    ...mapActions(["getTarget", "getTargets"]),
    //
    fetchTargets() {
      this.pageLoading = true;
      this.getTargets({ kind: "center", params: this.getParams }).then(() => {
        this.pageLoading = false;
      });

      // sync url path for page
      const incomingPath = `/repository/databank${this.getParams}`;
      if (this.$route.fullPath !== incomingPath) {
        return this.$router.push(incomingPath);
      }
    },
    onInputSearch(payload) {
      payload.input.model = payload.value;
      this.onSearching();
    },
    onSearching: debounce(function() {
      this.page = 1;
      this.fetchTargets();
    }, 1000),
    onChangeFilter(payload) {
      payload.input.model = payload.value;
      this.page = 1;
      this.fetchTargets();
    },
    onChangeSort(val) {
      const { primary, secondary } = this.sortInput;
      if (val.primary !== primary || val.secondary !== secondary) {
        this.sortInput = val;
        this.page = 1;
        this.fetchTargets();
      }
    },
    changePage(page) {
      if (page === this.page) return;
      this.page = page;
      this.fetchTargets();
    },
    changePerPage(perPage) {
      if (perPage === this.perPage) return;
      this.page = 1;
      this.perPage = perPage;
      this.fetchTargets();
    },
    //
    async onOpenPanel(target) {
      const timeout = this.selectedTarget ? 300 : 0;
      setTimeout(async () => {
        this.panelOpened = true;
        this.panelLoading = true;
        const params = { kind: "center", id: target.id };
        const { data } = await this.getTarget(params);
        this.panelLoading = false;
        this.selectedTarget = data;
      }, timeout);
    },
    onClosePanel() {
      this.panelOpened = false;
      setTimeout(() => {
        // this.selectedTarget = null;
      }, 300);
    },
    closePanelHandler(e) {
      let close = true;
      if (e.target && e.target.dataset && e.target.dataset.id) {
        const id = e.target.dataset.id || "";
        if (id === this.panelId) close = false;
      }
      if (close) {
        window.removeEventListener("click", this.closePanelHandler);
        this.onClosePanel();
      }
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/repository/Databank.vue